import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { GameReview } from '../types';
import { getGameReviews } from '../lib/game/reviewService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';

interface GameReviewsSummaryProps {
  gameId: string;
  gameName: string;
  gameSlug?: string;
}

const GameReviewsSummary: React.FC<GameReviewsSummaryProps> = ({ gameId, gameName, gameSlug }) => {
  const [reviews, setReviews] = useState<GameReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useAppTheme();

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const data = await getGameReviews(gameId);
        
        // Ordenar por saldo de votos (upvotes - downvotes)
        const sortedData = data.sort((a, b) => {
          const aBalance = (a.upvotes || 0) - (a.downvotes || 0);
          const bBalance = (b.upvotes || 0) - (b.downvotes || 0);
          return bBalance - aBalance; // Ordem decrescente
        });
        
        setReviews(sortedData);
      } catch (err) {
        console.error('Erro ao buscar avaliações:', err);
        setError('Não foi possível carregar as avaliações.');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [gameId]);

  const handleViewProfile = (username?: string) => {
    if (!username) return;
    navigate(`/u/${username}`);
  };

  if (loading) {
    return (
      <div className={`mt-8 rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Avaliações dos Usuários</h2>
        </div>
        <div className="animate-pulse flex space-x-4 mb-4">
          <div className={`rounded-full h-12 w-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
          <div className="flex-1 space-y-2">
            <div className={`h-4 rounded w-3/4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            <div className={`h-4 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            <div className={`h-4 rounded w-5/6 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`mt-8 rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Avaliações dos Usuários</h2>
        </div>
        <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800'}`}>
          {error}
        </div>
      </div>
    );
  }

  // Criar URL para a página de avaliações
  const reviewsUrl = gameSlug 
    ? `/games/titulo/${gameSlug}/avaliacoes` 
    : `/games/${gameId}/avaliacoes`;

  return (
    <div className={`mt-8 rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Avaliações dos Usuários</h2>
        <Link 
          to={reviewsUrl}
          className={`inline-flex items-center gap-2 font-medium ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
        >
          <MessageSquare size={18} />
          {reviews.length > 0 
            ? `Ver todas (${reviews.length})` 
            : "Seja o primeiro a avaliar"}
        </Link>
      </div>

      {reviews.length === 0 ? (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
          <MessageSquare className={`h-12 w-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className="mb-2">Nenhuma avaliação disponível para este jogo.</p>
          <p>Compartilhe sua opinião e ajude outros jogadores!</p>
        </div>
      ) : (
        <div>
          {/* Mostrar apenas as primeiras 2 avaliações */}
          {reviews.slice(0, 2).map((review) => (
            <div key={review.id} className={`mb-4 pb-4 last:border-0 ${theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-100'}`}>
              <div className="flex items-center gap-3 mb-2">
                <img 
                  src={review.avatar_url || `https://ui-avatars.com/api/?name=${review.username || 'Usuário'}&background=random${theme === 'dark' ? '&color=fff&background=374151' : ''}`} 
                  alt={review.username || 'Usuário'} 
                  className="w-10 h-10 rounded-full cursor-pointer"
                  onClick={() => review.username && handleViewProfile(review.username)}
                />
                <div>
                  <p 
                    className={`font-medium cursor-pointer ${theme === 'dark' ? 'text-gray-200 hover:text-indigo-400' : 'hover:text-indigo-600'}`}
                    onClick={() => review.username && handleViewProfile(review.username)}
                  >{review.username || 'Usuário'}</p>
                  <div className={`flex items-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {review.rating && (
                      <div className="flex items-center mr-2">
                        <Star className="h-4 w-4 text-yellow-400 mr-1" />
                        <span>{review.rating}</span>
                      </div>
                    )}
                    <span>{new Date(review.created_at).toLocaleDateString('pt-BR')}</span>
                    
                    {/* Adicionar contador de votos */}
                    {(review.upvotes || review.downvotes) && (
                      <div className={`flex items-center ml-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        <span className="mx-1">•</span>
                        <div className="flex items-center">
                          <ThumbsUp size={12} className="mr-1" />
                          <span className="mr-1">{review.upvotes || 0}</span>
                        </div>
                        <div className="flex items-center ml-1">
                          <ThumbsDown size={12} className="mr-1" />
                          <span>{review.downvotes || 0}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <p className={`line-clamp-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{review.review_text}</p>
            </div>
          ))}

          {reviews.length > 2 && (
            <div className="text-center mt-4">
              <Link 
                to={reviewsUrl}
                className={`inline-block px-4 py-2 rounded-md transition-colors ${theme === 'dark' ? 'bg-indigo-600/30 text-indigo-300 hover:bg-indigo-500/40' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}
              >
                Ver todas as {reviews.length} avaliações
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GameReviewsSummary; 